import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  REVIEW_GROUPS,
  type RegistryScope,
  type ReviewGroupResult,
  type ReviewReport,
} from 'shared';
import { api } from '../api';
import { useAuth } from '../auth';
import { ChangeList } from '../components/ChangeList';
import { Note } from '../components/Note';
import { StatStrip } from '../components/StatStrip';
import { DISPLAY, tokens } from '../theme';

/**
 * Màn Rà soát — spec SC-17 (đối chiếu chéo) và SC-19 (dòng thời gian thay đổi).
 *
 * Hai spec, một màn hình, vì chúng trả lời hai nửa của cùng một câu hỏi: *"có gì
 * sai trong danh sách của tôi mà từng trang riêng lẻ không nói ra được"*. Nửa đầu
 * so các trang với nhau, nửa sau so một trang với chính nó ở lần quét trước.
 *
 * Cố ý KHÔNG tách thành hai mục điều hướng: thanh trên cùng hiện có bốn đích, thêm
 * hai nữa là chạm ngưỡng phải chuyển sang sidebar (xem ghi chú ở `App.tsx`).
 */

function GroupBlock({ group }: { group: ReviewGroupResult }) {
  const meta = REVIEW_GROUPS[group.code];
  const sach = group.issues.length === 0;
  const nang = meta.severity === 'nghiem-trong';

  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={!sach && nang}
      sx={{
        border: `1px solid ${tokens.line}`,
        borderRadius: '10px !important',
        mb: 1.2,
        '&::before': { display: 'none' },
        // Nhóm sạch để trắng, nhóm có vấn đề mới tô nền — nếu tô hết thì màu không
        // còn phân biệt được gì.
        bgcolor: sach ? '#fff' : nang ? tokens.roseBg : tokens.amberBg,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flex: 1, pr: 1 }}>
          {sach ? (
            <CheckCircleIcon sx={{ fontSize: 19, color: tokens.okText, flex: 'none' }} />
          ) : (
            <Box
              sx={{
                flex: 'none',
                minWidth: 22,
                height: 22,
                px: 0.6,
                borderRadius: '11px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: nang ? tokens.rose : tokens.amber,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {group.soTrang}
            </Box>
          )}
          <Typography sx={{ fontSize: 14, fontWeight: sach ? 400 : 600, flex: 1 }}>
            {meta.label}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.muted, flex: 'none' }}>
            {sach ? 'không có vấn đề' : `${group.soTrang} trang`}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2, bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: 13, color: tokens.muted, mb: sach ? 0 : 1.5, pt: 1.5 }}>
          {meta.hauQua}
        </Typography>

        {group.issues.map((issue, i) => (
          <Box
            key={i}
            sx={{
              border: `1px solid ${tokens.line2}`,
              borderRadius: '8px',
              p: 1.4,
              mb: 1,
              '&:last-of-type': { mb: 0 },
            }}
          >
            {issue.giaTri && (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  mb: 0.8,
                  wordBreak: 'break-word',
                }}
              >
                “{issue.giaTri}”
              </Typography>
            )}
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              {issue.pages.map((p) => (
                <Button
                  key={p.slug}
                  component={RouterLink}
                  to={`/pages/${p.slug}`}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'none', fontSize: 12.5, py: 0.2 }}
                >
                  {p.code}
                </Button>
              ))}
            </Stack>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

function DoiChieu({ report }: { report: ReviewReport }) {
  const tongVanDe = report.groups.reduce((s, g) => s + g.issues.length, 0);
  const trangDinh = new Set(
    report.groups.flatMap((g) => g.issues.flatMap((i) => i.pages.map((p) => p.slug))),
  ).size;

  return (
    <Box>
      <StatStrip
        items={[
          { value: String(report.tongSoTrang), label: 'Trang trong phạm vi' },
          {
            value: String(tongVanDe),
            label: 'Vấn đề tìm được',
            color: tongVanDe > 0 ? tokens.roseText : tokens.okText,
          },
          { value: String(trangDinh), label: 'Trang có liên quan' },
          {
            value: String(report.chuaQuet),
            label: 'Chưa quét nên chưa tính',
            color: report.chuaQuet > 0 ? tokens.amberText : undefined,
          },
        ]}
      />

      {/* "Không tìm thấy vấn đề nào" trên một danh sách mà một nửa chưa quét là câu
          trả lời sai (spec SC-17 §4 Y7). */}
      {report.chuaQuet > 0 && (
        <Box sx={{ mt: 2 }}>
          <Note tone="warn">
            Có <b>{report.chuaQuet} trang chưa quét lần nào</b> nên không nằm trong phép đối chiếu
            này. Quét chúng rồi rà soát lại để có kết quả đầy đủ.
          </Note>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {report.groups.map((g) => (
          <GroupBlock key={g.code} group={g} />
        ))}
      </Box>
    </Box>
  );
}

export function RaSoatPage() {
  const { user } = useAuth();
  const laAdmin = user?.role === 'WEB_ADMINISTRATOR';
  const [scope, setScope] = useState<RegistryScope>('mine');
  const [chiNghiemTrong, setChiNghiemTrong] = useState(false);

  const report = useQuery({
    queryKey: ['review', 'doi-chieu', scope],
    queryFn: () => api.reviewReport(scope),
  });

  const changes = useQuery({
    queryKey: ['review', 'thay-doi', scope, chiNghiemTrong],
    queryFn: () => api.changeFeed({ scope, onlySerious: chiNghiemTrong, limit: 50 }),
  });

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="space-between">
        <Box>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 700 }}>
            Rà soát cả danh sách
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: tokens.muted, mt: 0.3 }}>
            Những vấn đề chỉ lộ ra khi xếp các trang cạnh nhau, và những gì vừa thay đổi.
          </Typography>
        </Box>

        {laAdmin && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={scope}
            onChange={(_, v: RegistryScope | null) => v && setScope(v)}
            sx={{ flex: 'none' }}
          >
            <ToggleButton value="mine" sx={{ textTransform: 'none', fontSize: 13, px: 1.6 }}>
              Trang của tôi
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: 13, px: 1.6 }}>
              Tất cả
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.3 }}>
          Đối chiếu giữa các trang
        </Typography>
        <Typography sx={{ fontSize: 13, color: tokens.muted, mb: 2 }}>
          Sáu phép so mà việc chấm điểm từng trang không làm được, vì mỗi lần chấm chỉ nhìn đúng
          một trang.
        </Typography>

        {report.isLoading && <CircularProgress size={22} />}
        {report.error && <Alert severity="error">{(report.error as Error).message}</Alert>}
        {report.data && <DoiChieu report={report.data} />}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px' }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.3 }}>
              Thay đổi gần đây
            </Typography>
            <Typography sx={{ fontSize: 13, color: tokens.muted }}>
              So từng trang với chính nó ở lần quét ngay trước.
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={chiNghiemTrong}
            onChange={(_, v: boolean | null) => v !== null && setChiNghiemTrong(v)}
            sx={{ flex: 'none' }}
          >
            <ToggleButton value={false} sx={{ textTransform: 'none', fontSize: 13, px: 1.6 }}>
              Tất cả
            </ToggleButton>
            <ToggleButton value={true} sx={{ textTransform: 'none', fontSize: 13, px: 1.6 }}>
              Chỉ nghiêm trọng
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Không ghi câu này là để người dùng hiểu nhầm rằng hệ thống đang canh giúp
            họ (spec SC-19 §4 Y6). */}
        <Box sx={{ mb: 2 }}>
          <Note>
            Thay đổi chỉ được phát hiện <b>khi có người bấm quét</b>. Hệ thống chưa tự quét định
            kỳ, nên danh sách trống không có nghĩa là không có gì thay đổi.
          </Note>
        </Box>

        {changes.isLoading && <CircularProgress size={22} />}
        {changes.error && <Alert severity="error">{(changes.error as Error).message}</Alert>}
        {changes.data &&
          (changes.data.length === 0 ? (
            <Typography sx={{ fontSize: 13.5, color: tokens.muted, py: 2, textAlign: 'center' }}>
              Chưa ghi nhận thay đổi nào{chiNghiemTrong ? ' ở mức nghiêm trọng' : ''}.
            </Typography>
          ) : (
            <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: '10px', overflow: 'hidden' }}>
              <ChangeList changes={changes.data} showPage />
            </Box>
          ))}
      </Paper>
    </Stack>
  );
}
